import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import omit from 'lodash/omit';
import { message } from 'antd';

import {
  createBranchRequest,
  createServiceRequest,
  deleteBranchRequest,
  deleteServiceRequest,
  fetchAgentsRequest,
  fetchBranchServicesRequest,
  fetchManageableBranches,
  fetchOfferingsRequest,
  fetchServiceTypesRequest,
  getBranchRequest,
  updateBranchRequest,
  updateServiceRequest,
} from '@/services/api/location';
import { getChangeLoadings, setChangeLoadingsUtility } from '@/utils/form';
import { trackModalView, trackOnlineEditForm } from '@/services/tracker';
import { LOCATION_EDIT_ACTION } from '@/constants/trackerActions';
import { processAPIResponse } from '@/utils/request';
import { fetchAvailableTimezones, fetchUserBranches } from '@/services/api/user';
import { selectBranchDetail } from '@/selectors/locationSelectors';
import { updateEntityExtraField } from '@/utils/extraFields';
import { getIntlTranslation, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { extractUsersFromBranches, resolveBranchIdsForServices } from '@/utils/location';
import { mergeAndSaveListParams } from '@/utils/listParams';

// Based on the discussion, we don't cache fetching single branches for now. By websocket changes, we can check the behavior again.
const CACHING_SINGLE_BRANCH_ENABLED = false;

// Local tracking for pending branch requests (not stored in Redux state)
const pendingBranchRequests = new Map<string, boolean>();

const initialState = {
  selectedBranch: {},
  list: [],
  timezones: [],
  changeLoadings: {},
  userBranches: [],
  agentList: [],
  branchDrawer: {
    branch: {},
    visible: false,
    branchId: '',
    toggledAt: 0,
  },
  newService: {},
  serviceTypes: [],
  fullBranchesList: [],
  allBranchesList: [],

};
export default {
  namespace: 'location',

  state: {
    selectedService: {},
    list: [],
    changeLoadings: {},
    userBranches: [],
    offeringList: [],
    agentList: [],
    manageableBranches: [],
    manageableBranchesList: {},
    manageableBranchesListParams: { page_size: 10 },
    branchDrawer: {
      branch: {},
      visible: false,
      branchId: '',
      toggledAt: 0,
    },
    serviceTypeList: [],
    newService: {},
    branchDetails: {}, // Mapping of branchId -> branch details
    fullBranchesList: [],
    allBranchesList: [],
  },

  effects: {
    * init({}, { put }) {
      const actions = ['fetch'];
      for (const action of actions) {
        yield put({
          type: action,
        });
      }
    },
    * getTimeZones({ payload = {} }, { call, put }) {
      const { response, data } = yield call(fetchAvailableTimezones, {});
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            yield put({
              type: 'setTimeZones',
              payload: data?.data,
            });

          },
          * onError() {
          },
        },
      );
    },
    getManageableBranches: [
      function* ({ payload = {} }, { call, put, select }) {
        const mergedPayload = yield* mergeAndSaveListParams(
          { select, put },
          (state: any) => state.location.manageableBranchesListParams,
          'savedManageableBranchesListParams',
          payload,
        );

        let query: any = {
          with: 'staffCount,availability,offering',
        };
        if (mergedPayload.search) {
          query.search = mergedPayload.search;
        }
        if (mergedPayload.offering !== undefined) {
          query.offering = mergedPayload.offering;
        }
        if (mergedPayload.page) {
          query.page = mergedPayload.page;
        }
        if (mergedPayload.limit) {
          query.page_size = mergedPayload.limit;
        }
        if (mergedPayload.page_size) {
          query.page_size = mergedPayload.page_size;
        }
        const { response, data } = yield call(fetchManageableBranches, { query });
        yield processAPIResponse(
          { response, data },
          {
            * onSuccess(data) {
              const users = extractUsersFromBranches(data?.data);
              if (users.length > 0) {
                yield put({
                  type: 'user/saveUsers',
                  payload: users,
                });
              }
              yield put({
                type: 'fetchedManageableBranches',
                payload: data,
              });
            },
            * onError() {
            },
          },
        );
      },
      {
        type: 'takeLatest',
      },
    ],
    * fetchFullBranchesWithDetails({ payload = {} }, { call, put, select }) {
      let query = { full: 1, in_scope: 1, page_size: payload.pageSize || 30 };

      if (payload.page) {
        query.page = payload.page;
      }
      if (payload.branches) {
        query.branches = payload.branches;
      }

      const FullBranchesAPICall = yield call(fetchUserBranches, { query });
      yield processAPIResponse(FullBranchesAPICall, {
        * onSuccess(data) {
          const users = extractUsersFromBranches(data);
          if (users.length > 0) {
            yield put({
              type: 'user/saveUsers',
              payload: users,
            });
          }
          yield put({
            type: 'fetchedFullBranchesList',
            payload: data,
          });
        },
        * onError() {
        },
      }, 'data');
    },
    * getAllBranchList(_, { call, put }) {
      // `with=servicesCount` enriches each branch with `services_count` so selectors
      // can hide branches that have no services without loading full branch details.
      const AllBranchesAPICall = yield call(fetchUserBranches, { query: { all: 1, in_scope: 1, with: 'servicesCount' } });
      yield processAPIResponse(AllBranchesAPICall, {
        * onSuccess(data) {
          const users = extractUsersFromBranches(data);
          if (users.length > 0) {
            yield put({
              type: 'user/saveUsers',
              payload: users,
            });
          }
          yield put({
            type: 'fetchedAllBranchList',
            payload: data,
          });
          if (data.length > 0 && data.length < 6) {
            yield put({
              type: 'fetchFullBranchesWithDetails',
              payload: {
                branches: data.map(branch => branch.id).join(','),
              },
            });
          }
        },
        * onError() {
        },
      }, 'data');
    },
    * getBranch({ payload: { branchId, scoped, lazy } }, { call, put, select }) {
      if (!branchId) return;
      if (!Array.isArray(branchId)) {
        branchId = [branchId];
      }
      for (const id of branchId) {
        if (CACHING_SINGLE_BRANCH_ENABLED || lazy) {
          // Check if there's already a pending request for this branchId using local tracking
          if (pendingBranchRequests.get(id)) {
            continue; // Don't make another request if one is already in progress
          }

          const existingBranch = yield select(selectBranchDetail, id);
          if (existingBranch && existingBranch.updatedAt) {
            const updatedAt = new Date(existingBranch.updatedAt);
            const now = new Date();
            const timeDiff = (now.getTime() - updatedAt.getTime()) / 1000;

            if (timeDiff < 60) {
              continue;
            }
          }
        }

        // Set pending state using local tracking
        pendingBranchRequests.set(id, true);

        const fn = scoped ? fetchUserBranches : getBranchRequest;
        const payload = scoped ? { query: { branches: id, full: 1, in_scope: 1 } } : { params: { branchId: id } };
        const { response, data } = yield call(fn, payload);

        yield processAPIResponse(
          { response, data },
          {
            * onSuccess(data) {
              const users = extractUsersFromBranches(data?.data);
              if (users.length > 0) {
                yield put({
                  type: 'user/saveUsers',
                  payload: users,
                });
              }

              if (Array.isArray(data?.data)) {
                const branchesArray = data.data;
                for (let i = 0; i < branchesArray.length; i++) {
                  const branch = branchesArray[i];
                  yield put({
                    type: 'setBranchDetail',
                    payload: { branchId: branch.id, branchData: branch },
                  });
                }
              } else {
                yield put({
                  type: 'setBranchDetail',
                  payload: { branchId: branchId[0], branchData: data.data },
                });
              }

              // Clear pending state on success
              pendingBranchRequests.delete(id);
            },
            * onError() {
              // Clear pending state on error
              pendingBranchRequests.delete(id);
            },
          },
        );
      }

    },
    // Reaction to SERVICE_AGENTS_CHANGED (see PubSubContext/events.ts): pull the
    // touched branches fresh so the calendar's agent columns match the new
    // assignment. `scoped: true` returns the same shape the calendar reads, and no
    // `lazy` — the 60s freshness skip would swallow exactly this refetch.
    * refetchBranchesForServices({ payload = {} }, { put, select }) {
      const locationState = yield select((state: any) => state.location);
      const branchIds = resolveBranchIdsForServices(locationState, payload);
      if (branchIds.length === 0) return;

      yield put({
        type: 'getBranch',
        payload: { branchId: branchIds, scoped: true },
      });
    },
    * fetchOfferings(_, { call, put }) {
      const { response, data } = yield call(fetchOfferingsRequest);
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            yield put({
              type: 'setOfferingList',
              payload: data?.data,
            });
          },
          * onError() {
          },
        },
      );
    },
    * fetchServiceTypes(_, { call, put }) {
      const { response, data } = yield call(fetchServiceTypesRequest);
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            yield put({
              type: 'setServiceTypeList',
              payload: data?.data,
            });
          },
          * onError() {
          },
        },
      );
    },
    * fetchAgents({}, { call, put }) {
      const { response, data } = yield call(fetchAgentsRequest, {});
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            if (data?.data && Array.isArray(data.data)) {
              yield put({
                type: 'user/saveUsers',
                payload: data.data,
              });
            }
            yield put({
              type: 'setAgentList',
              payload: data,
            });
          },
          * onError(error) {
          },
        },
      );
    },
    * fetch({ all }, { call, put, select }) {
      const { response, data } = yield call(fetchBranchServicesRequest, { params: { all } });
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            const users = extractUsersFromBranches(data?.data);
            if (users.length > 0) {
              yield put({
                type: 'user/saveUsers',
                payload: users,
              });
            }
            yield put({
              type: 'saveBranchServicesList',
              payload: data,
            });
          },
          * onError(error) {
          },
        },
      );
    },
    * createBranch({ payload, cb, intl }, { call, put, select }) {

      payload.is_active = get(payload, 'is_active', true);
      const { response, data } = yield call(createBranchRequest, { payload });
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            cb && cb(data?.data, true);
            message.success(
              getIntlTranslation(intl, {
                action: INTL_ACTION.CREATE_SUCCESS,
                entity: INTL_ENTITY.BRANCH,
              }),
            );
            yield put({
              type: 'getManageableBranches',
            });
          },
          * onError(error) {
            message.error(
              getIntlTranslation(intl, {
                action: INTL_ACTION.CREATE_FAILED,
                entity: INTL_ENTITY.BRANCH,
              }),
            );
          },
        },
      );
    },
    * deleteBranch({ payload: { branchId, cb, intl } }, { call, put }) {
      const { response, data } = yield call(deleteBranchRequest, { params: { branchId } });
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess() {
            yield put({
              type: 'toggleBranchDrawer',
            });
            yield put({
              type: 'getManageableBranches',
            });
            yield put({
              type: 'fetch',
            });
            cb && cb(data);
          },
          * onError(error) {
            message.error(
              error?.message || getIntlTranslation(intl, {
                action: INTL_ACTION.REMOVE_FAILED,
                entity: INTL_ENTITY.BRANCH,
              }),
            );
          },
        },
      );
    },
    * updateBranch({ payload: { ...body } }, { call, put, select }) {
      yield put({
        type: 'setChangeLoadings',
        payload: getChangeLoadings(body, true),
      });
      const payload = omit(body, ['branchId']);
      const { response, data } = yield call(updateBranchRequest, {
        params: { branchId: body.branchId },
        payload,
      });
      yield put({
        type: 'setChangeLoadings',
        payload: getChangeLoadings(body, false),
      });

      yield put({
        type: 'setBranchDetail',
        payload: { branchId: body.branchId, branchData: payload },
      });

      yield processAPIResponse(
        { response, data }, {
          * onSuccess(data) {
            const branch = data?.branch;
            if (branch) {
              yield put({
                type: 'setBranchDetail',
                payload: { branchId: branch.id, branchData: branch },
              });
            }
          },
        }, 'data',
      );
    },

    * createService({ payload }, { call, put, select }) {
      const cb = payload?.cb;
      const payloadData = payload?.data;
      if (isEmpty(payloadData.agents)) {
        payloadData.agents = [];
      }

      payloadData.calendar = {};

      const { response, data } = yield call(createServiceRequest, {
        payload: { ...payloadData, calendar: { ...payloadData.calendar, process_type: 'instant' } },
      });
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            const cb = payload?.cb;
            cb && cb(data?.data);
            yield put({
              type: 'getBranch',
              payload: {
                branchId: data?.data?.branch?.id,
              },
            });
            yield put({
              type: 'getManageableBranches',
            });
          },
        },
      );
    },
    * deleteService({ payload: { serviceId, cb, intl } }, { call, put }) {
      const { response, data } = yield call(deleteServiceRequest, { params: { serviceId } });
      yield processAPIResponse(
        { response, data },
        {
          * onSuccess() {
            yield put({
              type: 'getManageableBranches',
            });
            intl &&
            message.success(getIntlTranslation(intl, {
              entity: INTL_ENTITY.SERVICE,
              action: INTL_ACTION.REMOVE_SUCCESS,
            }));
            cb && cb(data);
          },
          * onError(error) {
            message.error(
              error?.message || getIntlTranslation(intl, {
                entity: INTL_ENTITY.SERVICE,
                action: INTL_ACTION.REMOVE_FAILED,
              }),
            );
          },
        },
      );
    },
    * updateService({ payload: { id, ...body } }, { call, put, select }) {
      const cb = body.cb;
      const fetchBranch = body.fetchBranch;
      const intl = body.intl;
      const payloadData = body.data;

      if (payloadData.service_type?.uuid)
        payloadData.service_type = payloadData.service_type?.uuid;
      else
        delete payloadData['service_type'];

      if (payloadData.agents?.length > 0)
        payloadData.agents = payloadData.agents.map(agent => typeof agent === 'string' ? agent : agent?.id);

      if (payloadData.managers?.length > 0)
        payloadData.managers = payloadData.managers.map(agent => typeof agent === 'string' ? agent : agent?.id);

      const { response, data } = yield call(updateServiceRequest, {
        params: { serviceId: id },
        payload: { ...payloadData },
      });
      yield put({
        type: 'setChangeLoadings',
        payload: getChangeLoadings(body, false),
      });

      trackOnlineEditForm(body, LOCATION_EDIT_ACTION);

      yield processAPIResponse(
        { response, data },
        {
          * onSuccess(data) {
            cb && cb(data, true);
            if (fetchBranch)
              yield put({
                type: 'fetch',
              });
            if (body.Availability) {
              yield put({
                type: 'updateDrawerLocation',
                payload: {
                  location: data?.data,
                },
              });
            }
          },
          * onError(error) {
            message.error(
              error?.message || getIntlTranslation(intl, {
                entity: INTL_ENTITY.SERVICE,
                action: INTL_ACTION.REMOVE_FAILED,
              }),
            );
            cb && cb(error, false);
          },
        },
      );
    },

  },
  reducers: {
    fetchedManageableBranches(state, action) {
      return {
        ...state,
        manageableBranches: action.payload?.data || [],
        manageableBranchesList: action.payload,
      };
    },
    savedManageableBranchesListParams(state, action) {
      return {
        ...state,
        manageableBranchesListParams: action.payload,
      };
    },
    saveBranchServicesList(state, action) {
      return {
        ...state,
        list: action.payload.data,
      };
    },
    setAgentList(state, action) {
      return {
        ...state,
        agentList: action.payload,
      };
    },
    setOfferingList(state, action) {
      return {
        ...state,
        offeringList: action.payload,
      };
    },
    setServiceTypeList(state, action) {
      return {
        ...state,
        serviceTypeList: action.payload,
      };
    },
    setBranchDetail(state, action) {
      let { branchId, branchData } = action.payload;
      const currentList = state.fullBranchesList || [];
      if (!branchData.id) branchData.id = branchId;
      branchData.updatedAt = Date.now();

      // Update branchDetails
      const updatedBranchDetails = {
        ...(state.branchDetails || {}),
        [branchId]: {
          ...(state.branchDetails ? state.branchDetails[branchId] || {} : {}),
          ...branchData,
        },
      };

      // Update fullBranchesList - use same replace/add logic as fetchedFullBranchesList
      const updatedList = [...currentList];

      const existingIndex = updatedList.findIndex(existingBranch => existingBranch.id === branchId);
      if (existingIndex !== -1) {
        // Replace existing branch
        updatedList[existingIndex] = {
          ...(updatedList[existingIndex] || {}),
          ...branchData,
        };
      } else {
        // Add new branch
        updatedList.push(branchData);
      }
      return {
        ...state,
        branchDetails: updatedBranchDetails,
        fullBranchesList: updatedList,
      };
    },
    setNewService(state, action) {
      return {
        ...state,
        newService: action.payload,
      };
    },
    setTimeZones(state, action) {
      return {
        ...state,
        timezones: action.payload,
      };
    },
    setChangeLoadings(state, action) {
      return setChangeLoadingsUtility(state, action);
    },
    setBranchToDrawer(state, action) {
      const payload = action?.payload;
      const branch = payload?.branch;
      const drawer = { ...state.branchDrawer };

      if (branch) {
        drawer.branchId = branch.id;
        drawer.branch = branch;
        trackModalView('location-details');
      }
      return {
        ...state,
        branchDrawer: drawer,
      };
    },
    toggleBranchDrawer(state, action) {
      const payload = action?.payload;
      const branch = payload?.branch;
      const service = payload?.service;
      const agent = payload?.agent;
      const drawer = { ...state.branchDrawer };

      if (branch) {
        drawer.toggledAt = new Date().getTime();
        drawer.branchId = branch.id;
        drawer.branch = branch;
        drawer.service = service;
        drawer.agent = agent;
        drawer.addService = payload?.addService;
        drawer.visible = true;
        trackModalView('location-details');
      } else {
        drawer.visible = false;
      }
      return {
        ...state,
        branchDrawer: drawer,
      };
    },
    toggleServiceTypeDrawer(state, action) {
      return {
        ...state,
        serviceTypeDrawer: {
          ...(action.payload || {}),
          toggledAt: new Date().getTime(),
        },
      };
    },
    fetchedAllBranchList(state, action) {
      return {
        ...state,
        allBranchesList: action.payload,
      };
    },
    fetchedFullBranchesList(state, action) {
      const currentTimestamp = new Date().toISOString();
      const currentList = state.fullBranchesList || [];
      const updatedBranchDetails = { ...(state.branchDetails || {}) };

      if (!Array.isArray(action.payload)) {
        return state;
      }

      const updatedList = [...currentList];

      action.payload.forEach(newBranch => {
        if (!newBranch.id) return;

        const existingIndex = updatedList.findIndex(existingBranch => existingBranch.id === newBranch.id);

        if (existingIndex !== -1) {
          updatedList[existingIndex] = newBranch;
        } else {
          updatedList.push(newBranch);
        }

        updatedBranchDetails[newBranch.id] = {
          ...newBranch,
          updatedAt: currentTimestamp,
        };
      });


      return {
        ...state,
        fullBranchesList: updatedList,
        branchDetails: updatedBranchDetails,
      };
    },
    updateBranchExtraField(state, action) {
      const { branchId, extraFieldTypeId, value, fieldType } = action.payload;
      if (!branchId) return state;

      const branch = state.branchDetails?.[branchId] || {};
      const newBranch = updateEntityExtraField(branch, extraFieldTypeId, value, fieldType);

      // Update fullBranchesList as well
      const updatedFullBranchesList = (state.fullBranchesList || []).map(b =>
        b.id === branchId ? newBranch : b,
      );

      return {
        ...state,
        branchDetails: {
          ...state.branchDetails,
          [branchId]: newBranch,
        },
        fullBranchesList: updatedFullBranchesList,
      };
    },
    updateBranchServiceExtraField(state, action) {
      const { branchServiceId, extraFieldTypeId, value, fieldType } = action.payload;
      if (!branchServiceId) return state;

      // Find the branch containing this service
      let targetBranchId = null;
      let targetService = null;

      for (const branchId in state.branchDetails) {
        const branch = state.branchDetails[branchId];
        const service = branch.services?.find(s => s.id === branchServiceId);
        if (service) {
          targetBranchId = branchId;
          targetService = service;
          break;
        }
      }

      if (!targetBranchId || !targetService) return state;

      // Update the service with new extra field using shared utility
      const updatedService = updateEntityExtraField(targetService, extraFieldTypeId, value, fieldType);

      // Update the branch with updated service
      const branch = state.branchDetails[targetBranchId];
      const updatedServices = (branch.services || []).map(s =>
        s.id === branchServiceId ? updatedService : s,
      );

      const updatedBranch = {
        ...branch,
        services: updatedServices,
      };

      // Update fullBranchesList as well
      const updatedFullBranchesList = (state.fullBranchesList || []).map(b =>
        b.id === targetBranchId ? updatedBranch : b,
      );

      return {
        ...state,
        branchDetails: {
          ...state.branchDetails,
          [targetBranchId]: updatedBranch,
        },
        fullBranchesList: updatedFullBranchesList,
      };
    },
    reset() {
      return initialState;
    },
  },
};
